import { type Container, createIdentifier } from '@blocksuite/global/di';
import { Extension } from '@blocksuite/store';

/**
 * Progress callback for tus uploads.
 * @param bytesUploaded - Bytes uploaded so far
 * @param bytesTotal - Total bytes to upload
 */
export type UploadProgressCallback = (
  bytesUploaded: number,
  bytesTotal: number
) => void;

/**
 * Direct tus upload service — bypasses BlobEngine to avoid loading entire file into RAM.
 *
 * When tus is enabled on the backend, this provider:
 * 1. Uploads the raw File object via tus (no arrayBuffer, no SHA on client)
 * 2. Backend computes SHA-256 (native crypto) and returns it via X-Blob-Key header
 *
 * When tus is disabled or missing, callers must fail instead of falling back to
 * the old BlobEngine file path, because it hashes via Blob.arrayBuffer().
 */
export interface ITusDirectUploadService {
  /**
   * Persist a file locally and schedule its authoritative tus upload.
   * Resolves once the local copy is durable, so document references can render
   * offline without storing a second binary copy in the outbox.
   */
  stage(
    file: File,
    onProgress?: UploadProgressCallback,
    docId?: string
  ): Promise<string | null>;

  /**
   * Upload a file directly via tus, bypassing BlobEngine.
   * Returns the blob key (SHA-256 hash) on success, or null if tus is not enabled.
   * A null result is terminal for streamable upload callers.
   *
   * @param docId - Optional document ID for BlobDocMapping (doc-level RBAC).
   *                When provided, the backend creates an immediate blob→doc mapping.
   */
  upload(
    file: File,
    onProgress?: UploadProgressCallback,
    docId?: string
  ): Promise<string | null>;
}

export const TusDirectUploadProvider =
  createIdentifier<ITusDirectUploadService>('TusDirectUploadService');

/**
 * Default (no-op) implementation — always returns null. StreamLain upload
 * callers must treat null as unavailable instead of using a full-buffer
 * blobSync fallback. The frontend overrides this with a real tus implementation.
 */
export class TusDirectUploadService
  extends Extension
  implements ITusDirectUploadService
{
  async stage(
    _file: File,
    _onProgress?: UploadProgressCallback,
    _docId?: string
  ): Promise<string | null> {
    return null;
  }

  async upload(
    _file: File,
    _onProgress?: UploadProgressCallback,
    _docId?: string
  ): Promise<string | null> {
    return null;
  }

  static override setup(di: Container) {
    di.addImpl(TusDirectUploadProvider, TusDirectUploadService);
  }
}
